import React, { RefObject, useState } from "react";

import classnames from "classnames";
import PlacesAutocomplete, {
  geocodeByPlaceId,
  getLatLng,
} from "react-places-autocomplete";

import { useTranslation } from "components/i18n/source";
import { Autocomplete, Button, Modal, Span, Text } from "components/ui/source";

import { IconX } from "icons";

import styles from "./SearchByPhrase.module.scss";

export type DestinationType = {
  label?: string;
  value?: string;
};

export type SearchByPhraseProps = {
  selected?: DestinationType;
  buildingName?: string;
  getLabel?(destination: DestinationType): string;
  getIcon?(destination: DestinationType): React.ReactNode;
  getValue?(destination: DestinationType): string;
  inputValue?: string;
  isMobile?: boolean;
  isOpen?: boolean;
  mapCenter?: google.maps.LatLngLiteral;
  onBlur?(): void;
  onChange?(value: string): void;
  onClear?(): void;
  onClearNeighborhood?(): void;
  onClick?(): void;
  onFocus?(): void;
  onSelectedChange?(destination: string): void;
  places: Record<string, unknown>[];
  mapBounds: Record<string, unknown>;
  setInputRef?(ref: RefObject<HTMLInputElement>): void;
  v2: boolean;
};

export function SearchByPhrase({
  selected = {},
  buildingName = null,
  places = null,
  isOpen,
  isMobile,
  inputValue,
  getValue,
  getLabel,
  getIcon,
  onClick,
  onFocus,
  onChange,
  onSelectedChange,
  onBlur,
  onClear,
  onClearNeighborhood,
  setInputRef,
  mapCenter,
  mapBounds = null,
  v2 = false,
}: SearchByPhraseProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(selected);

  const modalProps = {
    isOpen: modalOpen,
    onRequestOpen: () => setModalOpen(true),
    onRequestClose: () => setModalOpen(false),
    contentLabel: "Search By Address Or Area Modal",
    className: styles.modal_class,
  };

  const handleItemSelect = item => {
    if (v2) {
      setSelectedItem(item);
    }

    if (!inputValue) {
      setModalOpen(false);
      return onSelectedChange(item);
    }

    geocodeByPlaceId(item.placeId)
      .then(results => getLatLng(results[0]))
      .then(latLng => {
        onSelectedChange({
          geoCoordinates: latLng,
          ...item,
        });
        setModalOpen(false);
      })
      .catch(error =>
        // eslint-disable-next-line no-console
        console.error("Error getting latlng from place id", error),
      );
  };

  const clearSelection = () => {
    setSelectedItem(null);
    onClearNeighborhood();
    onClear();
  };

  const dropdownHeader = () => {
    const translateKey = inputValue
      ? "components.searchByPhrase.searchByAddress"
      : "components.searchByPhrase.topNeighborhoods";

    return (
      <div className={styles.top_neighborhood}>
        <Text
          translateKey={translateKey}
          // @ts-expect-error xxs is deprecated
          types={["xxs", "capitalized", "medium", "grey-1"]}
        />
      </div>
    );
  };

  const renderSearchByPhraseInput = (dropdownOpen = true) => (
    <PlacesAutocomplete
      value={inputValue}
      onChange={onChange}
      debounce={500}
      searchOptions={{
        location: {
          lat: () => mapCenter.lat,
          lng: () => mapCenter.lng,
        },
        radius: 5000, // distance in meters
        fields: ["formatted_address"],
        ...(mapBounds && { bounds: mapBounds }),
        strictbounds: true,
      }}
    >
      {({ getInputProps, suggestions }) => {
        const items = inputValue ? suggestions.slice(0, 5) : places;

        // Disable autocomplete on Chrome
        // https://stackoverflow.com/questions/29432665/autocomplete-off-is-completely-ignored
        const inputProps = getInputProps({ autoComplete: "neighborhoods" });

        return (
          <>
            <label className="sr-only" id="search-by-phrase-autocomplete-label">
              {t("components.searchByPhrase.placeholder")}
            </label>

            <Autocomplete
              id="search-by-phrase-autocomplete"
              inputValue={inputValue}
              items={items}
              noIcon={true}
              placeholder={t("components.searchByPhrase.placeholder")}
              size="sm"
              dropdownHeader={dropdownHeader()}
              isOpen={isOpen && dropdownOpen}
              filterOnCurrentInput={false}
              getValue={getValue}
              getLabel={getLabel}
              getIcon={getIcon}
              onClick={onClick}
              onFocus={onFocus}
              onChange={onChange}
              onSelectedChange={handleItemSelect}
              inputProps={inputProps}
              onBlur={onBlur}
              setInputRef={setInputRef}
              showCloseButton
              onClear={clearSelection}
            />
            {isMobile && renderSearchByPhraseResult(items)}
          </>
        );
      }}
    </PlacesAutocomplete>
  );

  const renderSearchByPhraseButton = () => (
    <div
      className={classnames(
        styles.button_container,
        buildingName && buildingName == inputValue && styles.inverted,
      )}
    >
      <Button
        type="no_background"
        onClick={() => setModalOpen(true)}
        centered={false}
      >
        {inputValue ? (
          inputValue
        ) : (
          <Text
            translateKey="components.searchByPhrase.placeholder"
            types={["sm", "truncate"]}
          />
        )}
      </Button>
    </div>
  );

  const renderSearchByPhraseV2Button = () => {
    // Selecting "all_neighborhoods" item equals to no selection
    const isPhraseSet =
      !!buildingName ||
      (!!selectedItem && getValue(selectedItem) !== "all_neighborhoods");

    return (
      <div
        id="neighborhoods_filter_button"
        className={classnames(
          styles.button_container_v2,
          isPhraseSet && styles.inverted,
        )}
      >
        <Button
          type={isPhraseSet ? "height_auto" : "dropdown"}
          width="fit-content"
          onClick={() => setModalOpen(true)}
          upperCase={false}
        >
          <Text className={styles.date_label} types={["body1", "inline"]}>
            {isPhraseSet
              ? buildingName || getLabel(selectedItem)
              : t("components.searchByPhrase.placeholder_v2")}
          </Text>
        </Button>
        {isPhraseSet && (
          <Button
            type="height_auto"
            width="fit-content"
            onClick={() => clearSelection()}
          >
            <IconX size="xs" className={styles.button_icon} />
          </Button>
        )}
      </div>
    );
  };

  const withBoldedMatchingChars = label => (
    <>
      {/* NB: we're assuming the items' value and labels have the same length
      even though value is in snake_case and label is normal case */}
      <Span types={["medium"]}>{label.substring(0, inputValue.length)}</Span>
      {label.substring(inputValue.length)}
    </>
  );

  const renderSearchByPhraseModal = () => {
    return (
      <Modal modalProps={modalProps}>
        <div className={styles.modal_header}>
          <Text
            translateKey="components.searchByPhrase.placeholder"
            // @ts-expect-error xs is deprecated
            types={["xs", "medium"]}
          />
          <Button
            type="no_background"
            onClick={() => setModalOpen(false)}
            centered={false}
          >
            <Text
              translateKey="components.searchByPhrase.close"
              types={["sm", "cancel"]}
            />
          </Button>
        </div>
        <div className={styles.modal_content}>
          {renderSearchByPhraseInput(isMobile)}
        </div>
      </Modal>
    );
  };

  const renderSearchByPhraseResult = items => {
    const subHeaderKey = inputValue
      ? "components.searchByPhrase.searchByAddress"
      : "components.searchByPhrase.topNeighborhoods";
    return (
      <>
        <div className={styles.modal_sub_header}>
          <Text
            translateKey={subHeaderKey}
            // @ts-expect-error xs is deprecated
            types={["xs", "capitalized", "medium", "cancel"]}
          />
        </div>
        <ul className={styles.results_list}>
          {items.map((item, index) => {
            const label = getLabel(item);
            const styledLabel = inputValue
              ? withBoldedMatchingChars(label)
              : label;

            return (
              <li className={styles.results_list_item} key={index}>
                <Button
                  type="no_background"
                  onClick={() => handleItemSelect(item)}
                  onKeyDown={() => handleItemSelect(item)}
                  centered={false}
                >
                  <Text types={["sm"]}>{styledLabel}</Text>
                </Button>
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  const renderMobileSearchByPhrase = () => (
    <>
      {v2 ? renderSearchByPhraseV2Button() : renderSearchByPhraseButton()}
      {renderSearchByPhraseModal()}
    </>
  );

  return isMobile ? renderMobileSearchByPhrase() : renderSearchByPhraseInput();
}
